import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useNotify, useTranslate } from 'react-admin';

import ErrorPage403 from 'src/pages/error/403';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { downloadBlob } from 'src/utils/format';

import CopyCell from '../../components/CopyCell';
import { DashboardV3RangeSelector } from '../../components/DashboardV3RangeSelector';
import { useDashboardV3DepositData } from '../../hooks/useDashboardV3DepositData';
import { useDashboardV3Range } from '../../hooks/useDashboardV3Range';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { exportDashboardV3Deposits } from '../../services/dashboardV3.service';
import type {
    DashboardV3DepositProductType,
    DashboardV3DepositRangeValue,
    DashboardV3DepositRow,
    DashboardV3DepositStatus,
} from '../../services/dashboardV3.types';

const ROWS_PER_PAGE = 25;

const RANGE_OPTIONS = [
    { labelKey: 'module.dashboardV3.range.today', label: 'Today', value: 'today' },
    { labelKey: 'module.dashboardV3.range.yesterday', label: 'Yesterday', value: 'yesterday' },
    { labelKey: 'module.dashboardV3.range.sevenDays', label: '7D', value: '7d' },
    { labelKey: 'module.dashboardV3.range.thirtyDays', label: '30D', value: '30d' },
    { labelKey: 'module.dashboardV3.range.custom', label: 'Custom', value: 'custom' },
    { labelKey: 'module.dashboardV3.range.all', label: 'All', value: 'all' },
] as const satisfies readonly {
    labelKey: string;
    label: string;
    value: DashboardV3DepositRangeValue;
}[];

const PRODUCT_TABS = [
    {
        labelKey: 'module.dashboardV3.productTabs.perps',
        label: 'Perpetuals',
        value: 'perps',
    },
    {
        labelKey: 'module.dashboardV3.productTabs.predictionSpot',
        label: 'Prediction Spot',
        value: 'prediction_spot',
    },
    {
        labelKey: 'module.dashboardV3.productTabs.predictionLeverage',
        label: 'Prediction Leverage',
        value: 'prediction_leverage',
    },
    {
        labelKey: 'module.dashboardV3.productTabs.predictionGrid',
        label: 'Prediction Grid',
        value: 'prediction_grid',
    },
] as const satisfies readonly {
    labelKey: string;
    label: string;
    value: DashboardV3DepositProductType;
}[];

const PRODUCT_COLORS: Record<DashboardV3DepositProductType, string> = {
    perps: '#FFB74D',
    prediction_spot: '#03DAC6',
    prediction_leverage: '#BB86FC',
    prediction_grid: '#81C995',
};

const STATUS_OPTIONS = [
    { value: '', labelKey: 'module.dashboardV3.deposits.filters.allStatuses', label: 'All Statuses' },
    { value: 'Success', labelKey: 'module.dashboardV3.deposits.status.success', label: 'Success' },
    { value: 'Failed', labelKey: 'module.dashboardV3.deposits.status.failed', label: 'Failed' },
    {
        value: 'Processing',
        labelKey: 'module.dashboardV3.deposits.status.processing',
        label: 'Processing',
    },
    { value: 'Refunded', labelKey: 'module.dashboardV3.deposits.status.refunded', label: 'Refunded' },
    {
        value: 'Reviewing',
        labelKey: 'module.dashboardV3.deposits.status.reviewing',
        label: 'Reviewing',
    },
    { value: 'Retrying', labelKey: 'module.dashboardV3.deposits.status.retrying', label: 'Retrying' },
] as const;

const INPUT_SX = {
    flexShrink: 0,
    '& .MuiOutlinedInput-root': {
        height: 38,
        borderRadius: '4px',
        color: '#FFFFFF',
        fontSize: 14,
        '& fieldset': { borderColor: 'rgba(255,255,255,0.16)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.28)' },
        '&.Mui-focused fieldset': { borderColor: '#BB86FC' },
    },
    '& .MuiInputLabel-root': {
        color: 'rgba(255,255,255,0.52)',
        '&.Mui-focused': { color: '#BB86FC' },
    },
    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.58)' },
    '& input::-webkit-calendar-picker-indicator': {
        filter: 'invert(1)',
        opacity: 0.65,
    },
};

function toUtcBoundary(date: string, boundary: 'start' | 'end') {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return undefined;
    return `${date}T${boundary === 'start' ? '00:00:00' : '23:59:59'}Z`;
}

function formatInteger(value: number) {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(
        Number.isFinite(Number(value)) ? Number(value) : 0
    );
}

function formatMoney(value: number) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(Number.isFinite(Number(value)) ? Number(value) : 0);
}

function formatAmount(value: number) {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 6,
    }).format(Number.isFinite(Number(value)) ? Number(value) : 0);
}

function formatDelta(value: number | null) {
    if (value == null || !Number.isFinite(Number(value))) return '-';
    const normalized = Object.is(Number(value), -0) ? 0 : Number(value);
    if (normalized === 0) return '0%';
    const amount = Math.abs(normalized).toFixed(2).replace(/\.?0+$/, '');
    return normalized > 0 ? `↑ +${amount}%` : `↓ ${amount}%`;
}

function deltaColor(value: number | null) {
    if (value == null || Number(value) === 0) return 'rgba(255,255,255,0.58)';
    return Number(value) > 0 ? '#81C995' : '#CF6679';
}

function formatUtcTime(value: string | null) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toISOString().replace('T', ' ').slice(0, 19);
}

function normalizeStatus(value: string | null) {
    return String(value || '').trim().toLowerCase();
}

function statusColor(value: string | null): 'default' | 'error' | 'info' | 'success' | 'warning' {
    const status = normalizeStatus(value);
    if (status === 'success') return 'success';
    if (status === 'failed') return 'error';
    if (status === 'refunded') return 'info';
    if (status === 'processing' || status === 'reviewing' || status === 'retrying') {
        return 'warning';
    }
    return 'default';
}

interface DepositKpiCardProps {
    title: string;
    value: string;
    delta: number | null;
    subtitle: string;
    accentColor: string;
    loading: boolean;
}

function DepositKpiCard({
    title,
    value,
    delta,
    subtitle,
    accentColor,
    loading,
}: DepositKpiCardProps) {
    const t = useTranslate();

    return (
        <Card
            sx={{
                minHeight: 172,
                bgcolor: '#232323',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 0.8,
                boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Box sx={{ position: 'absolute', inset: '0 0 auto', height: 3, bgcolor: accentColor }} />
            <CardContent sx={{ p: 2.5 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: 14, mb: 1.2 }}>
                    {title}
                </Typography>
                <Typography sx={{ color: '#FFFFFF', fontSize: 34, lineHeight: 1.2, mb: 1.1 }}>
                    {loading ? <Skeleton width={130} /> : value}
                </Typography>
                <Typography sx={{ color: deltaColor(delta), fontSize: 14, minHeight: 20, mb: 1 }}>
                    {loading ? (
                        <Skeleton width={150} />
                    ) : (
                        <>
                            {formatDelta(delta)}
                            <Box component="span" sx={{ color: 'rgba(255,255,255,0.58)', ml: 0.8 }}>
                                {t('module.dashboardV3.common.vsPrevious', { _: 'vs Previous' })}
                            </Box>
                        </>
                    )}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, lineHeight: 1.45 }}>
                    {subtitle}
                </Typography>
            </CardContent>
        </Card>
    );
}

export function DepositsPage() {
    const t = useTranslate();
    const notify = useNotify();
    const {
        range,
        setRange,
        customFrom,
        setCustomFrom,
        customTo,
        setCustomTo,
        rangeParams,
    } = useDashboardV3Range<DashboardV3DepositRangeValue>('7d');
    const [productType, setProductType] = useState<DashboardV3DepositProductType>('perps');
    const [page, setPage] = useState(0);
    const [userId, setUserId] = useState('');
    const [status, setStatus] = useState<DashboardV3DepositStatus | ''>('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [exporting, setExporting] = useState(false);
    const debouncedUserId = useDebouncedValue(userId.trim(), 500);

    const listStartDate = useMemo(() => toUtcBoundary(fromDate, 'start'), [fromDate]);
    const listEndDate = useMemo(() => toUtcBoundary(toDate, 'end'), [toDate]);
    const effectiveStatus = status || undefined;
    const kpiParams = useMemo(
        () => ({
            range: rangeParams.range,
            productType,
            startDate: rangeParams.startDate,
            endDate: rangeParams.endDate,
        }),
        [productType, rangeParams.endDate, rangeParams.range, rangeParams.startDate]
    );
    const listParams = useMemo(
        () => ({
            productType,
            page,
            size: ROWS_PER_PAGE,
            userId: debouncedUserId || undefined,
            status: effectiveStatus,
            startDate: listStartDate,
            endDate: listEndDate,
        }),
        [debouncedUserId, effectiveStatus, listEndDate, listStartDate, page, productType]
    );

    const { kpi, list, total, kpiLoading, listLoading } = useDashboardV3DepositData(
        kpiParams,
        listParams
    );

    useEffect(() => {
        setPage(0);
    }, [debouncedUserId, status, listStartDate, listEndDate, productType]);

    const productLabel = useMemo(() => {
        const product = PRODUCT_TABS.find((item) => item.value === productType);
        return product ? t(product.labelKey, { _: product.label }) : '';
    }, [productType, t]);

    const rangeLabel = useMemo(() => {
        const item = RANGE_OPTIONS.find((option) => option.value === range);
        return item ? t(item.labelKey, { _: item.label }) : '';
    }, [range, t]);

    const hasFilters = Boolean(userId || effectiveStatus || fromDate || toDate);
    const copiedText = t('module.dashboardV3.common.copied', { _: 'Copied' });

    const clearFilters = useCallback(() => {
        setUserId('');
        setStatus('');
        setFromDate('');
        setToDate('');
        setPage(0);
    }, []);

    const handleExport = useCallback(async () => {
        if (exporting) return;
        setExporting(true);

        try {
            const { blob, filename } = await exportDashboardV3Deposits({
                productType,
                userId: debouncedUserId || undefined,
                status: effectiveStatus,
                startDate: listStartDate,
                endDate: listEndDate,
                format: 'csv',
            });
            downloadBlob(filename, blob);
            notify(
                t('module.dashboardV3.deposits.messages.exportSuccess', {
                    _: 'CSV exported',
                }),
                { type: 'info' }
            );
        } catch (error: any) {
            notify(
                error?.message ||
                t('module.dashboardV3.deposits.messages.exportFailed', {
                    _: 'Export failed',
                }),
                { type: 'warning' }
            );
        } finally {
            setExporting(false);
        }
    }, [
        debouncedUserId,
        exporting,
        listEndDate,
        listStartDate,
        notify,
        productType,
        effectiveStatus,
        t,
    ]);

    const cards = [
        {
            title: t('module.dashboardV3.deposits.kpi.depositUsers', {
                range: rangeLabel,
                _: '%{range} Deposit Users',
            }),
            value: formatInteger(kpi.depositUsers),
            delta: kpi.depositUsersDelta,
            subtitle: t('module.dashboardV3.deposits.kpi.depositUsersSubtitle', {
                product: productLabel,
                _: 'Unique users who deposited to %{product} in the period',
            }),
            accentColor: '#BB86FC',
        },
        {
            title: t('module.dashboardV3.deposits.kpi.depositTransactions', {
                range: rangeLabel,
                _: '%{range} Deposit Transactions',
            }),
            value: formatInteger(kpi.depositTransactions),
            delta: kpi.depositTransactionsDelta,
            subtitle: t('module.dashboardV3.deposits.kpi.depositTransactionsSubtitle', {
                product: productLabel,
                _: 'Deposit transactions for %{product} in the period',
            }),
            accentColor: '#64B5F6',
        },
        {
            title: t('module.dashboardV3.deposits.kpi.depositAmount', {
                range: rangeLabel,
                _: '%{range} Deposit Amount',
            }),
            value: formatMoney(kpi.depositAmount),
            delta: kpi.depositAmountDelta,
            subtitle: t('module.dashboardV3.deposits.kpi.depositAmountSubtitle', {
                product: productLabel,
                _: 'Total %{product} deposit amount in the period',
            }),
            accentColor: '#FFB74D',
        },
    ];

    return (
        <PermissionGuard
            resource="dashboard-v3"
            action="read"
            fallback={<ErrorPage403 />}
        >
            <Box sx={{ mt: 3, color: '#FFFFFF' }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.6 }}>
                            {t('module.dashboardV3.deposits.title', { _: 'Deposit Data' })}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
                            {t('module.dashboardV3.deposits.subtitle', {
                                _: 'Deposits · Denominated in USDC  UTC+0',
                            })}
                        </Typography>
                    </Box>
                    <DashboardV3RangeSelector
                        value={range}
                        options={RANGE_OPTIONS}
                        customFrom={customFrom}
                        customTo={customTo}
                        onChange={setRange}
                        onCustomFromChange={setCustomFrom}
                        onCustomToChange={setCustomTo}
                    />
                </Stack>

                <Stack
                    direction="row"
                    spacing={4}
                    sx={{
                        mb: 2.5,
                        borderBottom: '1px solid rgba(255,255,255,0.12)',
                        flexWrap: 'wrap',
                    }}
                >
                    {PRODUCT_TABS.map((tab) => {
                        const active = tab.value === productType;
                        const color = PRODUCT_COLORS[tab.value];
                        return (
                            <Box
                                key={tab.value}
                                onClick={() => {
                                    setProductType(tab.value);
                                    setStatus('');
                                    setPage(0);
                                }}
                                sx={{
                                    py: 1.2,
                                    px: 0.5,
                                    cursor: 'pointer',
                                    color: active ? color : 'rgba(255,255,255,0.56)',
                                    fontSize: 14,
                                    fontWeight: active ? 700 : 600,
                                    borderBottom: active
                                        ? `2px solid ${color}`
                                        : '2px solid transparent',
                                }}
                            >
                                {t(tab.labelKey, { _: tab.label })}
                            </Box>
                        );
                    })}
                </Stack>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            lg: 'repeat(3, minmax(0, 1fr))',
                        },
                        gap: 2,
                        mb: 2.5,
                    }}
                >
                    {cards.map((card) => (
                        <DepositKpiCard key={card.title} {...card} loading={kpiLoading} />
                    ))}
                </Box>

                <Paper
                    sx={{
                        bgcolor: '#232323',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 0.8,
                        overflow: 'hidden',
                        boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{ px: 2, py: 2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {t('module.dashboardV3.deposits.table.title', {
                                _: 'Deposit Details',
                            })}
                        </Typography>
                        <Button
                            startIcon={
                                exporting ? (
                                    <CircularProgress size={16} sx={{ color: 'inherit' }} />
                                ) : (
                                    <FileDownloadOutlinedIcon />
                                )
                            }
                            onClick={handleExport}
                            disabled={exporting}
                            sx={{
                                color: '#BB86FC',
                                fontWeight: 700,
                                '&:hover': { bgcolor: 'rgba(187,134,252,0.08)' },
                                '&.Mui-disabled': { color: 'rgba(187,134,252,0.35)' },
                            }}
                        >
                            {exporting
                                ? t('module.dashboardV3.actions.exporting', { _: 'Exporting...' })
                                : t('module.dashboardV3.actions.exportCsv', { _: 'Export CSV' })}
                        </Button>
                    </Stack>

                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{
                            px: 2,
                            py: 2,
                            flexWrap: 'wrap',
                            rowGap: 1.5,
                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                        }}
                    >
                        <TextField
                            size="small"
                            value={userId}
                            onChange={(event) => setUserId(event.target.value)}
                            placeholder={t('module.dashboardV3.deposits.filters.userId', {
                                _: 'Search User ID',
                            })}
                            sx={{ ...INPUT_SX, width: 230 }}
                        />
                        <TextField
                            select
                            size="small"
                            label={t('module.dashboardV3.deposits.filters.status', {
                                _: 'Status',
                            })}
                            value={status}
                            onChange={(event) =>
                                setStatus(event.target.value as DashboardV3DepositStatus | '')
                            }
                            InputLabelProps={{ shrink: true }}
                            sx={{ ...INPUT_SX, width: 170 }}
                        >
                            {STATUS_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {t(option.labelKey, { _: option.label })}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            size="small"
                            type="date"
                            label={t('module.dashboardV3.deposits.filters.from', { _: 'From' })}
                            value={fromDate}
                            onChange={(event) => setFromDate(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ ...INPUT_SX, width: 180 }}
                        />
                        <TextField
                            size="small"
                            type="date"
                            label={t('module.dashboardV3.deposits.filters.to', { _: 'To' })}
                            value={toDate}
                            onChange={(event) => setToDate(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ ...INPUT_SX, width: 180 }}
                        />
                        {hasFilters && (
                            <Button
                                startIcon={<CloseIcon />}
                                onClick={clearFilters}
                                sx={{ color: '#BB86FC', minHeight: 38 }}
                            >
                                {t('module.dashboardV3.actions.clear', { _: 'Clear' })}
                            </Button>
                        )}
                    </Stack>

                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: 1050 }}>
                            <TableHead>
                                <TableRow>
                                    {[
                                        ['userId', 'User ID', 180],
                                        ['walletAddress', 'Wallet Address', 200],
                                        ['depositAmount', 'Deposit Amount (USDC)', 180],
                                        ['depositTxHash', 'Deposit TxHash', 220],
                                        ['status', 'Status', 130],
                                        ['time', 'Time', 180],
                                    ].map(([key, label, minWidth]) => (
                                        <TableCell
                                            key={String(key)}
                                            align={key === 'depositAmount' ? 'right' : 'left'}
                                            sx={{
                                                minWidth,
                                                bgcolor: '#263646',
                                                color: 'rgba(255,255,255,0.76)',
                                                fontWeight: 700,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {t(`module.dashboardV3.deposits.table.${key}`, {
                                                _: label,
                                            })}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {listLoading ? (
                                    Array.from({ length: 5 }).map((_, index) => (
                                        <TableRow key={`deposit-skeleton-${index}`}>
                                            {Array.from({ length: 6 }).map((__, cellIndex) => (
                                                <TableCell key={cellIndex}>
                                                    <Skeleton />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : list.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={6}
                                            align="center"
                                            sx={{ py: 8, color: 'rgba(255,255,255,0.5)' }}
                                        >
                                            {t('module.dashboardV3.common.noData', { _: 'No data' })}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    list.map((row: DashboardV3DepositRow, index) => (
                                        <TableRow
                                            key={`${row.depositTxHash || row.userId || 'deposit'}-${index}`}
                                            hover
                                            sx={{
                                                '& td': {
                                                    borderColor: 'rgba(255,255,255,0.07)',
                                                    color: 'rgba(255,255,255,0.8)',
                                                    fontSize: 13,
                                                    whiteSpace: 'nowrap',
                                                },
                                            }}
                                        >
                                            <TableCell>
                                                <CopyCell
                                                    text={row.userId}
                                                    copiedText={copiedText}
                                                    start={8}
                                                    end={6}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <CopyCell
                                                    text={row.walletAddress}
                                                    copiedText={copiedText}
                                                    start={8}
                                                    end={6}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    component="span"
                                                    sx={{ color: '#FFB74D', fontSize: 13 }}
                                                >
                                                    {formatAmount(row.depositAmount)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <CopyCell
                                                    text={row.depositTxHash}
                                                    copiedText={copiedText}
                                                    start={10}
                                                    end={8}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    color={statusColor(row.status)}
                                                    label={row.status || '-'}
                                                    sx={{
                                                        height: 24,
                                                        textTransform: 'capitalize',
                                                        fontWeight: 700,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>{formatUtcTime(row.time)}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, nextPage) => setPage(nextPage)}
                        rowsPerPage={ROWS_PER_PAGE}
                        onRowsPerPageChange={() => undefined}
                        rowsPerPageOptions={[ROWS_PER_PAGE]}
                        sx={{
                            color: 'rgba(255,255,255,0.72)',
                            borderTop: '1px solid rgba(255,255,255,0.07)',
                            '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.72)' },
                        }}
                    />
                </Paper>
            </Box>
        </PermissionGuard>
    );
}
